import React from "react";
import { twMerge } from "tailwind-merge";
import { type ComponentFormatter, formatComponentValue } from "../../utils/formatting";
import { truncateMiddle } from "../../utils/formatting/text";
import type { FieldValue } from "../../utils/formatting/types";
import { CopyToClipboard } from "../CopyToClipboard";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { Tooltip } from "../Tooltip";

/**
 * StatList — Compact key-value statistics display
 *
 * Displays formatted statistics with status indicators and optional copy functionality.
 * Uses the unified formatting system for consistent data presentation.
 */

// Types

export type StatTone = "neutral" | "success" | "warning" | "error" | "info";
export type StatAlign = "start" | "end";
export type StatLayout = "one-column" | "two-column";

export type StatValue = FieldValue | React.ReactElement;

export type StatFormatter = ComponentFormatter;

export interface StatThreshold {
  when: (value: StatValue) => boolean;
  tone: StatTone;
}

export interface StatItem {
  /** Unique identifier */
  id: string;
  /** Label for the statistic */
  label: React.ReactNode;
  /** Value to display */
  value: StatValue;
  /** Custom formatter for the value */
  formatter?: StatFormatter;
  /** Additional metadata below value */
  meta?: React.ReactNode;
  /** Explicit tone/status color */
  tone?: StatTone;
  /** Data-driven tone based on value */
  thresholds?: StatThreshold[];
  /** Enable copy to clipboard */
  copyable?: boolean | ((value: StatValue) => string);
  /** Position of the copy icon when copyable is enabled, defaults to "left" */
  copyIconPosition?: "left" | "right";
  /** Optional link URL */
  href?: string;
  /** Click handler */
  onAction?: () => void;
  /** Icon before label */
  iconLeft?: React.ReactNode;
  /** Icon after value */
  iconRight?: React.ReactNode;
  /** Tooltip for additional info */
  tooltip?: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Stack label above value on mobile screens (useful for multi-line values like tags) */
  stackOnMobile?: boolean;

  /**
   * PII type for masking (e.g., "email", "phone", "full-name")
   * When provided along with piiEntity, adds data-pii-* attributes for masking
   */
  piiType?: string;

  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   */
  piiEntity?: string;
}

export interface StatListProps {
  /** Statistics to display */
  items: StatItem[];
  /** Layout mode */
  layout?: StatLayout;
  /** Compact spacing */
  dense?: boolean;
  /** Value alignment */
  valueAlign?: StatAlign;
  /** Show dividers between items */
  showDividers?: boolean;
  /** Loading state */
  skeleton?: boolean;
  /** Empty state */
  empty?: boolean;
  /** Error state */
  error?: string | React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

// Helpers

// Use the centralized formatting utility
function formatValue(value: StatValue, formatter?: StatFormatter): React.ReactNode {
  // If value is already a React element, return it as-is (skip formatting)
  if (React.isValidElement(value)) {
    return value;
  }

  return formatComponentValue({
    value,
    formatter,
    emptyClassName: "text-text-muted",
    emptyText: "—",
  });
}

function getTone(item: StatItem): StatTone | undefined {
  // Check thresholds first
  if (item.thresholds && item.value !== null && item.value !== undefined) {
    for (const threshold of item.thresholds) {
      if (threshold.when(item.value)) {
        return threshold.tone;
      }
    }
  }

  // Fall back to explicit tone
  return item.tone;
}

const toneColors: Record<StatTone, string> = {
  neutral: "text-text-body",
  success: "text-feedback-success",
  warning: "text-feedback-warning",
  error: "text-feedback-error",
  info: "text-feedback-info",
};

// StatRow Component

// Constants for auto-truncation
const LONG_STRING_THRESHOLD = 24;
const LONG_STRING_TRUNCATE_LENGTH = 20;

function StatRow({ item, dense, valueAlign }: { item: StatItem; dense?: boolean; valueAlign?: StatAlign }) {
  const tone = getTone(item);
  const toneClass = tone ? toneColors[tone] : "";
  const isStacked = item.stackOnMobile;

  // Check if this is a long string that should be auto-truncated on mobile
  const isLongString =
    typeof item.value === "string" &&
    item.value.length > LONG_STRING_THRESHOLD &&
    !item.formatter &&
    !React.isValidElement(item.value);

  // Get the formatted value (used for both truncated and full display)
  const formattedValue = formatValue(item.value, item.formatter);

  // Get the truncated value for mobile display
  // Note: We cast to string here because isLongString already verifies typeof item.value === "string"
  const truncatedValue = isLongString ? truncateMiddle(item.value as string, LONG_STRING_TRUNCATE_LENGTH) : null;

  const textToCopy = typeof item.copyable === "function" ? item.copyable(item.value) : String(item.value);

  // PII data attributes (only set if both piiType and piiEntity are provided)
  const piiAttrs =
    item.piiType && item.piiEntity
      ? {
          "data-pii-type": item.piiType,
          "data-pii-entity": item.piiEntity,
        }
      : {};

  // For long strings, render both truncated (mobile) and full (desktop) versions
  // CSS classes control which is visible based on screen size
  // Tooltip content is wrapped in span with PII attributes to allow masking by Curtain extension
  const hasPiiAttrs = Object.keys(piiAttrs).length > 0;
  const valueDisplay = isLongString ? (
    <>
      {/* Mobile: show truncated with tooltip */}
      <Tooltip content={hasPiiAttrs ? <span {...piiAttrs}>{String(item.value)}</span> : String(item.value)}>
        <span className={twMerge("md:hidden", item.href ? "hover:underline cursor-pointer" : "")} {...piiAttrs}>
          {truncatedValue}
        </span>
      </Tooltip>
      {/* Desktop: show full value */}
      <span className={twMerge("hidden md:inline", item.href ? "hover:underline cursor-pointer" : "")} {...piiAttrs}>
        {formattedValue}
      </span>
    </>
  ) : (
    <span className={item.href ? "hover:underline cursor-pointer" : ""} {...piiAttrs}>
      {formattedValue}
    </span>
  );

  // Copy button component
  const copyButton = item.copyable ? (
    <CopyToClipboard value={textToCopy} size="sm">
      <span className="sr-only">Copy</span>
    </CopyToClipboard>
  ) : null;

  const hasCopyLeft = item.copyable && (item.copyIconPosition === "left" || !item.copyIconPosition);
  const hasCopyRight = item.copyable && item.copyIconPosition === "right";

  // When stacked on mobile, align value to left on mobile, restore alignment on sm+
  const valueAlignClass = isStacked
    ? valueAlign === "start"
      ? "text-left"
      : "text-left sm:text-right"
    : valueAlign === "start"
      ? "text-left"
      : "text-right";

  const valueContent = (
    <dd
      className={twMerge(
        dense ? "text-sm" : "text-base",
        "font-medium",
        valueAlignClass,
        toneClass,
        item.disabled && "opacity-50",
        "overflow-hidden min-w-0"
      )}
    >
      <div
        className={twMerge(
          "inline-flex items-center gap-1 max-w-full",
          valueAlign === "start" ? "justify-start" : "justify-end"
        )}
      >
        <span className="inline-flex items-center gap-1 min-w-0">
          {valueDisplay}
          {item.iconRight}
        </span>
        {hasCopyRight && copyButton}
      </div>

      {item.meta && (
        <div className={twMerge("text-xs text-text-muted mt-0.5", valueAlign === "start" ? "text-left" : "text-right")}>
          {item.meta}
        </div>
      )}
    </dd>
  );

  const content = (
    <>
      <dt
        className={twMerge(
          dense ? "text-xs" : "text-sm",
          "text-text-caption truncate",
          "flex items-center gap-1",
          item.disabled && "opacity-50"
        )}
      >
        {item.iconLeft}
        <span title={typeof item.label === "string" ? item.label : undefined}>{item.label}</span>
        {item.tooltip && <Icon name="Question" size={10} className="text-text-muted" aria-label="More information" />}
      </dt>

      {hasCopyLeft && <div className="flex items-center">{copyButton}</div>}

      {item.href ? (
        <a href={item.href} onClick={item.onAction} className="block">
          {valueContent}
        </a>
      ) : (
        valueContent
      )}
    </>
  );

  // Grid layout classes - when stacked on mobile, use single column on mobile, restore on sm+
  // Use minmax for label column to prevent collapse when values are wide
  const baseGridCols = hasCopyLeft
    ? "grid-cols-[minmax(120px,1fr)_auto_auto] gap-x-1"
    : dense
      ? "grid-cols-[minmax(120px,1fr)_1fr] gap-x-2"
      : "grid-cols-[minmax(120px,1fr)_1fr] gap-x-4";

  const stackedGridCols = hasCopyLeft
    ? "grid-cols-1 gap-y-1 sm:grid-cols-[minmax(120px,1fr)_auto_auto] sm:gap-x-1 sm:gap-y-0"
    : dense
      ? "grid-cols-1 gap-y-1 sm:grid-cols-[minmax(120px,1fr)_1fr] sm:gap-x-2 sm:gap-y-0"
      : "grid-cols-1 gap-y-1 sm:grid-cols-[minmax(120px,1fr)_1fr] sm:gap-x-4 sm:gap-y-0";

  // Use items-center for copyable rows (typically single-line values)
  // Use items-start for stacked or potentially wrapping content
  const alignmentClass = isStacked ? "items-start sm:items-center" : hasCopyLeft ? "items-center" : "items-start";

  return <div className={twMerge("grid", alignmentClass, isStacked ? stackedGridCols : baseGridCols)}>{content}</div>;
}

// Main Component

export function StatList({
  items,
  layout = "one-column",
  dense = false,
  valueAlign = "end",
  showDividers = false,
  skeleton = false,
  empty = false,
  error,
  className,
}: StatListProps) {
  // Loading state
  if (skeleton) {
    return (
      <div
        className={twMerge(
          "grid",
          layout === "two-column" ? "md:grid-cols-2 gap-x-6" : "grid-cols-1",
          dense ? "gap-y-1" : "gap-y-2",
          className
        )}
      >
        {Array.from({ length: items.length || 4 }).map((_, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] items-start gap-x-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    );
  }

  // Error or empty states
  if (error) {
    return <div className={twMerge("text-sm text-feedback-error", className)}>{error}</div>;
  }

  if (empty || !items.length) {
    return <div className={twMerge("text-sm text-text-muted", className)}>No data available</div>;
  }

  return (
    <dl
      className={twMerge(
        "grid",
        layout === "two-column" ? "md:grid-cols-2 gap-x-6" : "grid-cols-1",
        dense ? "gap-y-1" : "gap-y-2",
        className
      )}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          {showDividers && index > 0 && layout === "one-column" && (
            <div className="col-span-full border-t border-border-default -mt-1 mb-1" />
          )}
          <StatRow item={item} dense={dense} valueAlign={valueAlign} />
        </React.Fragment>
      ))}
    </dl>
  );
}
